import { getDb, type PendingOperation } from './db';
import { isOnline, onNetworkChange, reportConnectivityFailure } from './networkStatus';

let flushInProgress = false;
let initialized = false;
const flushCallbacks = new Set<() => void>();

function init() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  onNetworkChange((online) => {
    if (online) flush();
  });
}

export function onFlushComplete(fn: () => void): () => void {
  flushCallbacks.add(fn);
  return () => flushCallbacks.delete(fn);
}

// ---- Queue operations ----

export async function enqueue(op: Omit<PendingOperation, 'id' | 'created_at'>): Promise<void> {
  init();
  try {
    const db = await getDb();
    await db.pendingOps.add({ ...op, created_at: Date.now() });
  } catch {
    // IDB unavailable / Dexie import failed — без локальной очереди мутация
    // потеряется при reload. Это редкий edge case (Safari Private Mode и т.п.),
    // в нём офлайн-режим и так не работает.
  }
}

export async function getPendingCount(): Promise<number> {
  // IDB unavailable / aborted / Dexie not loaded — считаем что нечего синкать.
  // Возвращаем 0 вместо reject, чтобы вызов не утекал в unhandled-rejection если
  // caller забыл .catch (OfflineIndicator пуллит каждые 5 сек, легко
  // пересечься с закрытием таба).
  try {
    const db = await getDb();
    return await db.pendingOps.count();
  } catch {
    return 0;
  }
}

export async function getPendingOps(): Promise<PendingOperation[]> {
  try {
    const db = await getDb();
    return await db.pendingOps.orderBy('created_at').toArray();
  } catch {
    return [];
  }
}

// ---- Flush queue to server ----

export async function flush(): Promise<void> {
  if (flushInProgress || !isOnline()) return;
  flushInProgress = true;

  let flushedAny = false;

  // Внешний try/catch обязателен: flush() вызывается fire-and-forget
  // из 6 точек (SyncManager mount/visibility/SW-message, onNetworkChange,
  // AddEntryModal, VoteClient). Если db.pendingOps.toArray() / .delete()
  // падает с AbortError (таб закрывают во время idle-flush'а), без catch
  // это утекает в unhandled-rejection и спамит админа. AbortError здесь
  // означает «работа прервалась, ничего критичного» — следующий flush
  // на mount/visibility/reconnect подхватит очередь заново.
  try {
    const db = await getDb();
    const ops = await db.pendingOps.orderBy('created_at').toArray();
    for (const op of ops) {
      try {
        await executeOp(op);
        await db.pendingOps.delete(op.id!);
        flushedAny = true;
      } catch (err: unknown) {
        // network error — stop flushing, retry on next reconnect
        if (!isOnline()) break;

        const status = (err as { status?: number }).status;

        // Нет HTTP-статуса = ответ не пришёл вовсе (TypeError/timeout) —
        // это сетевая ошибка при «залипшем» онлайн-флаге, а не серверная.
        // Раньше она падала в ветку «skip» ниже и операция УДАЛЯЛАСЬ —
        // изменение терялось. Теперь: операцию сохраняем, останавливаем
        // flush и сообщаем networkStatus (он запустит цикл проб и повторит
        // flush, когда связь реально вернётся).
        if (status === undefined) {
          reportConnectivityFailure();
          break;
        }

        // 404 = entry deleted on server while offline — clean up local cache
        if (status === 404) {
          await cleanupDeletedEntity(op);
          await db.pendingOps.delete(op.id!);
          flushedAny = true;
          continue;
        }

        // other server errors — skip to avoid blocking queue
        console.warn('[offlineQueue] Failed op, removing:', op, err);
        await db.pendingOps.delete(op.id!);
        flushedAny = true;
      }
    }

    // Clean up offline stub entries (created while offline)
    if (flushedAny) {
      try {
        const stubs = await db.entries.where('id').startsWith('offline_').toArray();
        if (stubs.length) {
          await db.entries.bulkDelete(stubs.map((s) => s.id));
        }
      } catch {
        // non-critical
      }
    }
  } catch {
    // IDB AbortError (closed connection / aborted transaction), IDB unavailable,
    // или Dexie import failed — non-critical, тихо выходим. Очередь сохранилась
    // (или её нет — тогда и синкать нечего), следующий flush её подхватит.
  } finally {
    flushInProgress = false;
  }

  // Notify listeners to refresh data from server
  if (flushedAny) {
    flushCallbacks.forEach((fn) => {
      try { fn(); } catch { /* listener crash isolated */ }
    });
  }
}

async function cleanupDeletedEntity(op: PendingOperation): Promise<void> {
  try {
    const db = await getDb();
    if (op.table === 'entries' || op.table === 'entry_statuses') {
      await db.entries.delete(op.entity_id);
    } else if (op.table === 'categories') {
      await db.categories.delete(op.entity_id);
    }
  } catch {
    // non-critical
  }
}

async function executeOp(op: PendingOperation): Promise<void> {
  const { type, table, entity_id, payload } = op;

  if (table === 'entry_statuses') {
    const res = await fetch(`/api/entries/${entity_id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throwWithStatus(res.status);
    return;
  }

  if (table === 'categories' && type === 'update') {
    const res = await fetch(`/api/categories/${entity_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throwWithStatus(res.status);
    return;
  }

  if (table === 'entries') {
    if (type === 'create') {
      const res = await fetch('/api/entries/from-web', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throwWithStatus(res.status);
      return;
    }
    if (type === 'update') {
      const res = await fetch(`/api/entries/${entity_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throwWithStatus(res.status);
      return;
    }
    if (type === 'delete') {
      const res = await fetch(`/api/entries/${entity_id}`, { method: 'DELETE' });
      if (!res.ok && res.status >= 500) throwWithStatus(res.status);
      return;
    }
  }
}

function throwWithStatus(status: number): never {
  const err = new Error(`Server error ${status}`) as Error & { status: number };
  err.status = status;
  throw err;
}

// ---- Offline-aware API wrappers ----

// Apply a partial mutation to the cached entry in IndexedDB and notify
// listening shells (DashboardShell / NowShell etc) so they refresh their
// view from the cache. Without this, mutations like "сменить категорию"
// or "🔥" would update the server but the shell would later overwrite
// the fresh server response with a stale IDB snapshot.
//
// ВАЖНО: Dexie update пропускает поля со значением `undefined` (не записывает
// их), а `null` записывает явно. Поэтому для сброса decision/reaction
// «↩ Вернуть в ленту» нужен именно null, иначе запись «застрянет» в старой
// категории / в архиве.
async function applyEntryPatchToCache(
  entryId: string,
  patch: Partial<{ decision: string | null; reaction: string | null; category: string; category_id: string | null; title: string; body: string }>,
): Promise<void> {
  try {
    const db = await getDb();
    const entry = await db.entries.get(entryId);
    if (!entry) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await db.entries.update(entryId, patch as any);
  } catch {
    // IDB unavailable — non-critical
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('favbox:idb-changed'));
  }
}

export async function offlinePatchStatus(
  entryId: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  init();

  // Optimistic IDB patch — независимо от онлайна. Сервер после успешного PATCH
  // снова обновит локальный кеш через router.refresh + CacheSync, но между
  // mutation'ом и refresh'ем shell'ам нужен корректный срез из IDB, иначе
  // изменение «отскакивает» в UI.
  //
  // Передаём null явно (а не undefined) — иначе Dexie не сбросит поле в IDB
  // и «↩ Вернуть в ленту» не вернёт запись в неразобранное.
  const cachePatch: Partial<{ decision: string | null; reaction: string | null }> = {};
  if ('decision' in body) cachePatch.decision = body.decision as string | null;
  if ('reaction' in body) cachePatch.reaction = body.reaction as string | null;

  await applyEntryPatchToCache(entryId, cachePatch);

  if (isOnline()) {
    try {
      const res = await fetch(`/api/entries/${entryId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) {
        const data = await res.json();
        // Immediate-delete путь (retention=0 + decision=never): сервер удалил
        // запись полностью. Убираем из IDB чтобы клиентский кеш не показывал
        // фантом до следующего sync'a.
        if (data?.deleted) {
          try {
            const db = await getDb();
            await db.entries.delete(entryId);
          } catch {
            // non-critical
          }
          window.dispatchEvent(new CustomEvent('favbox:idb-changed'));
        }
        return data;
      }
      // if server error but we're online, still try queuing
    } catch {
      // network failed or timeout — fall through to queue
    }
  }

  // Offline or request failed — queue it
  await enqueue({
    type: 'update',
    table: 'entry_statuses',
    entity_id: entryId,
    payload: body,
  });

  return { ok: true, queued: true };
}

export async function offlineCreateEntry(
  text: string,
  categoryId?: string,
  categoryName?: string,
): Promise<{ entryId?: string; queued?: boolean; error?: string; suggestedCategory?: string; categoryAutoAssigned?: boolean; duplicate?: boolean; titleMissing?: boolean; resolvedTitle?: string }> {
  init();

  // Try server first when online to get metadata (titleMissing, resolvedTitle)
  if (isOnline()) {
    try {
      const res = await fetch('/api/entries/from-web', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, categoryId }),
        signal: AbortSignal.timeout(15000),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.duplicate) return { duplicate: true };
        return {
          entryId: data.entryId,
          suggestedCategory: data.suggestedCategory,
          categoryAutoAssigned: data.categoryAutoAssigned ?? false,
          titleMissing: data.titleMissing ?? false,
          resolvedTitle: data.resolvedTitle,
        };
      }
    } catch {
      // network failed or timeout — fall through to queue
    }
  }

  // Offline fallback: queue for later sync
  const tempId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  await enqueue({
    type: 'create',
    table: 'entries',
    entity_id: tempId,
    payload: { text, categoryId },
  });

  // Save a stub entry locally
  try {
    const db = await getDb();
    const isUrl = /^https?:\/\//i.test(text.trim());
    await db.entries.put({
      id: tempId,
      user_id: '',
      chat_id: '',
      message_text: isUrl ? undefined : text,
      url: isUrl ? text.trim() : undefined,
      title: isUrl ? text.trim() : text.slice(0, 80),
      category: categoryName ?? '',
      category_id: categoryId ?? null,
      tags: [],
      created_at: new Date().toISOString(),
      _cached_at: Date.now(),
    });
  } catch {
    // non-critical
  }

  return { entryId: tempId, queued: true };
}

export async function offlinePatchCategory(
  categoryId: string,
  payload: Record<string, unknown>,
): Promise<{ ok: boolean; queued?: boolean }> {
  init();

  if (isOnline()) {
    try {
      const res = await fetch(`/api/categories/${categoryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) return { ok: true };
    } catch {
      // network failed or timeout — fall through to queue
    }
  }

  await enqueue({
    type: 'update',
    table: 'categories',
    entity_id: categoryId,
    payload,
  });

  // Update local cache
  try {
    const db = await getDb();
    const cat = await db.categories.get(categoryId);
    if (cat) {
      const patch: Partial<{ name: string; emoji: string; is_visible: boolean; mode: 'queue' | 'collection' }> = {};
      if ('name' in payload) patch.name = payload.name as string;
      if ('emoji' in payload) patch.emoji = payload.emoji as string;
      if ('isVisible' in payload) patch.is_visible = payload.isVisible as boolean;
      if ('mode' in payload) patch.mode = payload.mode as 'queue' | 'collection';
      await db.categories.update(categoryId, patch);
    }
  } catch {
    // non-critical
  }

  return { ok: true, queued: true };
}

/**
 * PATCH entry fields (category, title, body) with offline queue.
 * Mirrors offlinePatchCategory: try server first, fall back to IndexedDB queue
 * + optimistic local cache update if network failed or we're offline.
 */
export async function offlinePatchEntry(
  entryId: string,
  payload: Record<string, unknown>,
): Promise<{ ok: boolean; queued?: boolean }> {
  init();

  // Optimistic IDB patch — независимо от онлайна. См. applyEntryPatchToCache.
  const cachePatch: Partial<{ category: string; category_id: string | null; title: string; body: string }> = {};
  if ('category' in payload) cachePatch.category = payload.category as string;
  if ('category_id' in payload) cachePatch.category_id = (payload.category_id as string | null) ?? null;
  if ('title' in payload) cachePatch.title = payload.title as string;
  if ('body' in payload) cachePatch.body = payload.body as string;
  await applyEntryPatchToCache(entryId, cachePatch);

  if (isOnline()) {
    try {
      const res = await fetch(`/api/entries/${entryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) return { ok: true };
    } catch {
      // network failed or timeout — fall through to queue
    }
  }

  await enqueue({
    type: 'update',
    table: 'entries',
    entity_id: entryId,
    payload,
  });

  return { ok: true, queued: true };
}
